"use client";

import { useEffect, useLayoutEffect, useRef, type ReactNode } from "react";
import gsap from "gsap";

// Shared editorial shell for the (auth) route group: mono/uppercase green
// eyebrow + large Inter sans 600 headline (masked lines, GSAP entrance) +
// mono check trust lines on the left, the white form panel on the right,
// and two slow-drifting tint blobs in the background. Purely presentational
// — every page keeps its own form logic and passes the card content as
// children. Styles live in ../auth.css (.lpa-*, scoped under .lp-root which
// the (auth) layout provides).
//
// Motion: GSAP timeline on mount (skipped for prefers-reduced-motion; the
// server-rendered base state is fully visible, so no JS = no blank page).
// The card's ambient halo is pure CSS: .lpa-card-wrap::before paints a
// blurred lime/mint radial glow behind the panel and breathes slowly
// (lpa-breathe keyframes; static under prefers-reduced-motion).

export function ArrowIcon() {
  return (
    <svg viewBox="0 0 14 10" fill="none" aria-hidden="true">
      <path
        d="M1 5h12m0 0L9 1m4 4L9 9"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Envelope glyph for the "Continue with email" action — the arrow on that
 *  button would imply "next page" when what actually happens is "we mail you". */
export function MailIcon() {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="2.2" y="4.2" width="15.6" height="11.6" rx="2.4" />
      <path d="m3.2 6 5.7 4.3a2 2 0 0 0 2.2 0L16.8 6" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true">
      <path d="M3 8.5 6.5 12 13 4.5" />
    </svg>
  );
}

/** Circle-arc progress spinner shown inside submit buttons while a request
 *  is in flight (CSS rotation via .lpa-spinner). */
export function SpinnerIcon() {
  return (
    <svg
      className="lpa-spinner"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M14.5 8A6.5 6.5 0 1 1 8 1.5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

/** Open-eye glyph for the password visibility toggle (hidden → show). */
export function EyeIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M2.5 12S6.2 5.5 12 5.5 21.5 12 21.5 12 17.8 18.5 12 18.5 2.5 12 2.5 12Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

/** Slashed-eye glyph for the password visibility toggle (shown → hide). */
export function EyeOffIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M4 4l16 16" />
      <path d="M10.7 5.7A10 10 0 0 1 12 5.5c5.8 0 9.5 6.5 9.5 6.5a17.8 17.8 0 0 1-2.7 3.5M6.5 6.9C4 8.8 2.5 12 2.5 12S6.2 18.5 12 18.5a9.6 9.6 0 0 0 4-.9" />
      <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
    </svg>
  );
}

/** Static mint ring + check badge (same look the animated SuccessIntro
 *  draws, without GSAP) — used by terminal success states like the
 *  forgot-password "check your inbox" panel. */
export function SuccessBadge() {
  return (
    <div className="lpa-success-badge">
      <svg viewBox="0 0 64 64" fill="none" aria-hidden="true">
        <circle className="lpa-success-ring" cx="32" cy="32" r="27" />
        <path className="lpa-success-check" d="M20 33.5 28 41.5 44 24" />
      </svg>
    </div>
  );
}

type AuthShellProps = {
  /** Mono uppercase micro-label above the headline. */
  eyebrow: string;
  /** Serif headline, ink. */
  title: string;
  /** Second headline line, green (serif em). */
  em: string;
  /** Muted paragraph under the headline. */
  subtitle?: ReactNode;
  /** Mono check trust lines (hidden on mobile). */
  trust?: string[];
  /** Form card content. */
  children: ReactNode;
};

export function AuthShell({
  eyebrow,
  title,
  em,
  subtitle,
  trust,
  children,
}: AuthShellProps) {
  const root = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return;
    }
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: "power3.out" } });
      tl.from('[data-fx="eyebrow"]', { y: 14, autoAlpha: 0, duration: 0.45 })
        .from(
          '[data-fx="line"]',
          { yPercent: 115, duration: 0.7, stagger: 0.12 },
          "-=0.25",
        )
        .from('[data-fx="sub"]', { y: 12, autoAlpha: 0, duration: 0.5 }, "-=0.35")
        .from(
          '[data-fx="trust"] li',
          { x: -14, autoAlpha: 0, duration: 0.4, stagger: 0.09 },
          "-=0.3",
        )
        // The card comes in EARLY and overlaps the left column's tail: it is
        // the reason the page exists, so it must never be the last thing to
        // land. Everything below it is proof the user reads afterwards.
        .from(
          '[data-fx="card"]',
          { y: 26, scale: 0.975, autoAlpha: 0, duration: 0.7 },
          "-=0.55",
        )
        .from('[data-fx="meta"]', { autoAlpha: 0, duration: 0.4 }, "-=0.3");
      // Ambient motion, independent of the entrance.
      gsap.to('[data-fx="blob1"]', {
        y: 30,
        x: 16,
        duration: 12,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
      });
      gsap.to('[data-fx="blob2"]', {
        y: -26,
        x: -18,
        duration: 14,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
      });
    }, root);
    return () => ctx.revert();
  }, []);

  return (
    <div className="lpa-split" ref={root}>
      <div className="lpa-blob lpa-blob--1" data-fx="blob1" aria-hidden="true" />
      <div className="lpa-blob lpa-blob--2" data-fx="blob2" aria-hidden="true" />
      <div className="lpa-intro">
        <p className="lpa-eyebrow" data-fx="eyebrow">
          {eyebrow}
        </p>
        <h1 className="lpa-h1">
          <span className="lpa-mask">
            <span className="lpa-mask-in" data-fx="line">
              {title}
            </span>
          </span>
          <span className="lpa-mask">
            <span className="lpa-mask-in" data-fx="line">
              <em>{em}</em>
            </span>
          </span>
        </h1>
        {subtitle ? (
          <p className="lpa-sub" data-fx="sub">
            {subtitle}
          </p>
        ) : null}

        {trust && trust.length > 0 ? (
          <ul className="lpa-trust" data-fx="trust">
            {trust.map((line) => (
              <li key={line}>
                <CheckIcon />
                <span>{line}</span>
              </li>
            ))}
          </ul>
        ) : null}


        <nav className="lpa-meta" data-fx="meta" aria-label="Site">
          <a href="/status">
            System status
            <ExternalIcon />
          </a>
          <span aria-hidden="true">·</span>
          <a href="/docs">Documentation</a>
          <span aria-hidden="true">·</span>
          <a href="/pricing">Pricing</a>
        </nav>
      </div>
      <div className="lpa-card-wrap">
        <section className="lp-panel lpa-card" data-fx="card">
          {children}
        </section>
        {/* Reassurance sits OUTSIDE the card: inside, it competes with the
            submit button for the eye at the exact moment we want a decision. */}
        <p className="lpa-card-note">
          <LockIcon />
          Encrypted in transit · data stored in Poland
        </p>
      </div>
    </div>
  );
}

function ExternalIcon() {
  return (
    <svg viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path
        d="M4.2 2.2h5.6v5.6M9.8 2.2 2.2 9.8"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <rect
        x="2.6"
        y="6.2"
        width="8.8"
        height="6.2"
        rx="1.6"
        stroke="currentColor"
        strokeWidth="1.4"
      />
      <path
        d="M4.8 6.2V4.6a2.2 2.2 0 0 1 4.4 0v1.6"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  );
}

/** Success/error flash box in the landing palette (mint / tinted danger),
 *  with a leading glyph so the intent scans instantly. */
export function AuthAlert({
  intent,
  children,
}: {
  intent: "success" | "error";
  children: ReactNode;
}) {
  return (
    <div
      className={`lpa-alert lpa-alert--${intent}`}
      role={intent === "error" ? "alert" : "status"}
    >
      {intent === "error" ? (
        <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path
            d="M8 1.8 15 14H1L8 1.8Z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinejoin="round"
          />
          <path
            d="M8 6.2v3.2"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
          <circle cx="8" cy="11.6" r="0.9" fill="currentColor" />
        </svg>
      ) : (
        <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <circle
            cx="8"
            cy="8"
            r="6.5"
            stroke="currentColor"
            strokeWidth="1.5"
          />
          <path
            d="M5.1 8.3 7.1 10.2 10.9 6.1"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
      <span>{children}</span>
    </div>
  );
}

/**
 * Presentational 4-segment password strength meter. Pure function of the
 * current password value (no validation coupling — zod still owns the rules).
 * Renders nothing until the user starts typing.
 */
export function StrengthMeter({ password }: { password: string }) {
  if (password.length === 0) return null;

  let score = 0;
  if (password.length >= 8) score += 1;
  if (password.length >= 12) score += 1;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password) && /\d/.test(password))
    score += 1;
  if (/[^a-zA-Z0-9]/.test(password)) score += 1;

  const tooShort = password.length < 6;
  const label = tooShort
    ? "Too short"
    : score <= 1
      ? "Weak"
      : score === 2
        ? "Okay"
        : score === 3
          ? "Good"
          : "Strong";
  const level = tooShort ? 1 : Math.min(4, Math.max(1, score));

  return (
    <>
      <div className="lpa-strength" data-level={level} aria-hidden="true">
        <span />
        <span />
        <span />
        <span />
      </div>
      <p className="lpa-hint" aria-live="polite">
        Password strength: {label}
      </p>
    </>
  );
}

/**
 * Replaces the form card's contents with a celebratory checkmark, then hands
 * control back via onDone so the caller can swap in the real form. Used on
 * /sign-in when arriving from a successful registration, instead of a static
 * "registered" banner sitting above the form.
 */
export function SuccessIntro({
  title,
  subtitle,
  onDone,
}: {
  title: string;
  subtitle?: string;
  onDone: () => void;
}) {
  const root = useRef<HTMLDivElement | null>(null);
  const onDoneRef = useRef(onDone);
  // Keep the latest onDone without retriggering the mount animation. Updated
  // in an effect (not during render) so render stays pure; every read happens
  // asynchronously (timeout / GSAP onComplete), long after effects have run.
  useEffect(() => {
    onDoneRef.current = onDone;
  });

  useLayoutEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      const t = window.setTimeout(() => onDoneRef.current(), 900);
      return () => window.clearTimeout(t);
    }
    const ctx = gsap.context(() => {
      const ring = root.current?.querySelector<SVGCircleElement>(
        '[data-fx="success-ring"]',
      );
      const check = root.current?.querySelector<SVGPathElement>(
        '[data-fx="success-check"]',
      );
      if (ring) {
        const len = ring.getTotalLength();
        gsap.set(ring, { strokeDasharray: len, strokeDashoffset: len });
      }
      if (check) {
        const len = check.getTotalLength();
        gsap.set(check, { strokeDasharray: len, strokeDashoffset: len });
      }

      const tl = gsap.timeline({
        defaults: { ease: "power3.out" },
        onComplete: () => onDoneRef.current(),
      });
      tl.from('[data-fx="success-badge"]', {
        scale: 0,
        autoAlpha: 0,
        duration: 0.55,
        ease: "back.out(1.8)",
      })
        .to(
          ring ?? [],
          { strokeDashoffset: 0, duration: 0.5, ease: "power2.inOut" },
          "-=0.15",
        )
        .to(
          check ?? [],
          { strokeDashoffset: 0, duration: 0.4, ease: "power2.out" },
          "-=0.1",
        )
        .fromTo(
          '[data-fx="success-ping"]',
          { scale: 0.6, autoAlpha: 0.5 },
          { scale: 1.7, autoAlpha: 0, duration: 0.9, ease: "power1.out" },
          "-=0.55",
        )
        .from(
          '[data-fx="success-text"]',
          { y: 12, autoAlpha: 0, duration: 0.4, stagger: 0.08 },
          "-=0.3",
        )
        .to({}, { duration: 0.9 })
        .to('[data-fx="success-wrap"]', {
          autoAlpha: 0,
          y: -10,
          scale: 0.97,
          duration: 0.35,
          ease: "power2.in",
        });
    }, root);
    return () => ctx.revert();
  }, []);

  return (
    <div ref={root} className="lpa-success" data-fx="success-wrap">
      <div className="lpa-success-badge" data-fx="success-badge">
        <span className="lpa-success-ping" data-fx="success-ping" aria-hidden="true" />
        <svg viewBox="0 0 64 64" fill="none" aria-hidden="true">
          <circle
            className="lpa-success-ring"
            data-fx="success-ring"
            cx="32"
            cy="32"
            r="27"
          />
          <path
            className="lpa-success-check"
            data-fx="success-check"
            d="M20 33.5 28 41.5 44 24"
          />
        </svg>
      </div>
      <p className="lpa-success-title" data-fx="success-text" role="status">
        {title}
      </p>
      {subtitle ? (
        <p className="lpa-success-sub" data-fx="success-text">
          {subtitle}
        </p>
      ) : null}
    </div>
  );
}
